import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  RefreshCw,
  Clock,
  Send,
  AlertCircle,
  CheckCircle2,
  PlayCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

/**
 * Admin panel — observability + control for the welcome auto-send sweep.
 *
 * Shows in real time:
 *   - How many leads still don't have the welcome flag set
 *   - When the next periodic sweep will run
 *   - Summary of the last sweep run (counts of sent/backfilled/failures…)
 *   - The list of leads currently waiting, sorted oldest-first
 *   - Per-run history with details (which outcome per lead)
 *
 * Controls:
 *   - "Lancer un check maintenant" → POST /welcome-sweep/run
 *   - Per-lead "Renvoyer" → POST /welcome-sweep/send/{lead_id}
 *
 * Polls /status every 30s so the count keeps shrinking in real time as the
 * sweep does its job.
 */

const OUTCOME_META = {
  sent: { label: "envoyé", color: "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40" },
  backfilled: { label: "déjà envoyé (backfill)", color: "text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-950/40" },
  deferred_quiet_hours: { label: "différé (heures calmes)", color: "text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/40" },
  skipped_disabled: { label: "désactivé", color: "text-zinc-600 bg-zinc-100 dark:text-zinc-400 dark:bg-zinc-900" },
  skipped_no_phone: { label: "pas de téléphone", color: "text-zinc-600 bg-zinc-100 dark:text-zinc-400 dark:bg-zinc-900" },
  failed_send: { label: "échec envoi", color: "text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-950/40" },
  exception: { label: "erreur technique", color: "text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-950/40" },
};

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (d.getTime() - Date.now()) / 1000;
    const abs = Math.abs(diff);
    if (abs < 60) return diff > 0 ? `dans ${Math.round(abs)}s` : `il y a ${Math.round(abs)}s`;
    if (abs < 3600) return diff > 0 ? `dans ${Math.round(abs / 60)} min` : `il y a ${Math.round(abs / 60)} min`;
    return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function WelcomeSweepPanel() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState({ items: [], total: 0 });
  const [history, setHistory] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sendingLead, setSendingLead] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [s, p, h] = await Promise.all([
        whatsappApi.welcomeSweep.status().catch(() => null),
        whatsappApi.welcomeSweep.pending({ limit: 100 }).catch(() => ({ items: [], total: 0 })),
        whatsappApi.welcomeSweep.history({ limit: 20 }).catch(() => ({ items: [] })),
      ]);
      setStatus(s);
      setPending(p);
      setHistory(h);
    } catch (e) {
      if (!silent) toast.error("Impossible de charger l'état du check welcome");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh every 30s so the count shrinks live as the sweep does its work
    const id = setInterval(() => load({ silent: true }), 30_000);
    return () => clearInterval(id);
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await whatsappApi.welcomeSweep.runSweep();
      toast.success(
        `Check terminé · ${r.sent} envoyés · ${r.backfilled} déjà envoyés · ${r.failures} échecs`
      );
      await load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du check");
    } finally {
      setRunning(false);
    }
  };

  const sendOne = async (lead) => {
    setSendingLead(lead.id);
    try {
      const r = await whatsappApi.welcomeSweep.sendOne(lead.id);
      if (r.status === "sent") {
        toast.success(`Welcome envoyé à ${lead.nom}`);
      } else if (r.status === "deferred") {
        toast.info(`Différé (heures calmes) — ${lead.nom}`);
      } else if (r.status === "skipped") {
        toast.info(`Welcome désactivé pour ce projet`);
      } else {
        toast.error(`Échec d'envoi à ${lead.nom}`);
      }
      await load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSendingLead(null);
    }
  };

  const last = status?.last_sweep;
  const missing = status?.leads_without_welcome ?? 0;
  const healthy = missing === 0;

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
      data-testid="welcome-sweep-panel"
    >
      <header className="mb-5 flex flex-wrap items-center gap-3">
        <div className={cn(
          "flex h-10 w-10 items-center justify-center rounded-xl",
          healthy
            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
            : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
        )}>
          {healthy ? <CheckCircle2 className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-bold">
            Vérification welcome
            {missing > 0 && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                {missing} à traiter
              </span>
            )}
            {healthy && (
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                Tout le monde a reçu
              </span>
            )}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Un check automatique tourne toutes les {status?.interval_minutes ?? 30} min
            pour s'assurer que chaque lead reçoit son message de bienvenue.
            {status?.next_run_at && (
              <> Prochain tick : <span className="font-medium">{formatRelative(status.next_run_at)}</span>.</>
            )}
          </p>
        </div>
        <Button
          onClick={runNow}
          disabled={running || loading}
          size="sm"
          className="gap-2"
          data-testid="welcome-sweep-run"
        >
          <PlayCircle className={cn("h-4 w-4", running && "animate-pulse")} />
          {running ? "Check en cours…" : "Lancer un check maintenant"}
        </Button>
      </header>

      {/* Last sweep summary */}
      {last && (
        <div className="mb-5 grid gap-2 rounded-xl border border-border/60 bg-muted/30 p-3 text-xs sm:grid-cols-6">
          <Stat label="Dernier check" value={formatRelative(last.ts)} />
          <Stat label="Analysés" value={last.scanned} />
          <Stat label="Envoyés" value={last.sent} highlight="emerald" />
          <Stat label="Déjà OK" value={last.backfilled} highlight="blue" />
          <Stat label="Différés" value={last.deferred} highlight="amber" />
          <Stat label="Échecs" value={last.failures} highlight={last.failures > 0 ? "red" : null} />
        </div>
      )}

      {/* Pending leads */}
      <div className="mb-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Leads en attente de welcome ({pending.total})
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => load()}
            disabled={loading}
            className="gap-1.5 text-xs"
          >
            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>
        {loading && pending.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            Chargement…
          </div>
        ) : pending.items.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            Tous les leads ont reçu leur message de bienvenue.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Lead</th>
                  <th className="px-3 py-2 text-left">Téléphone</th>
                  <th className="px-3 py-2 text-left">Projet</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-left">Créé</th>
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {pending.items.map((lead) => {
                  const stuck = lead.welcome_sent_at?.endsWith?.("#claimed");
                  return (
                    <tr
                      key={lead.id}
                      className="border-t border-border/40 transition-colors hover:bg-muted/30"
                      data-testid={`welcome-pending-${lead.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-heading font-semibold">{lead.nom || "—"}</div>
                        {lead.commercial && (
                          <div className="text-[10px] text-muted-foreground">
                            Comm. {lead.commercial}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap">
                        {lead.telephone || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {lead.programme_immobilier || (
                          <span className="italic text-muted-foreground">aucun</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">
                          {lead.statut}
                        </span>
                        {stuck && (
                          <span
                            className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                            title="Tentative en cours côté serveur"
                          >
                            en cours
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap">
                        {lead.created_at
                          ? new Date(lead.created_at).toLocaleDateString("fr-FR")
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendOne(lead)}
                          disabled={sendingLead === lead.id}
                          className="gap-1.5 h-7 text-xs"
                          data-testid={`welcome-send-${lead.id}`}
                        >
                          {sendingLead === lead.id ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {sendingLead === lead.id ? "…" : "Renvoyer"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History */}
      <div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          Historique des derniers checks ({history.items.length})
        </button>
        {showHistory && (
          <div className="mt-3 space-y-2">
            {history.items.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                Aucun check enregistré pour le moment.
              </p>
            ) : (
              history.items.map((run, i) => (
                <details
                  key={i}
                  className="rounded-lg border border-border/60 bg-card p-3 text-xs"
                >
                  <summary className="cursor-pointer flex flex-wrap items-center gap-2">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{formatRelative(run.ts)}</span>
                    <span className="text-muted-foreground">·</span>
                    <span>{run.scanned} analysés</span>
                    {run.sent > 0 && (
                      <span className="text-emerald-700 dark:text-emerald-400">{run.sent} envoyés</span>
                    )}
                    {run.backfilled > 0 && (
                      <span className="text-blue-700 dark:text-blue-400">{run.backfilled} backfill</span>
                    )}
                    {run.deferred > 0 && (
                      <span className="text-amber-700 dark:text-amber-400">{run.deferred} différés</span>
                    )}
                    {run.failures > 0 && (
                      <span className="text-red-700 dark:text-red-400">{run.failures} échecs</span>
                    )}
                  </summary>
                  {Array.isArray(run.details) && run.details.length > 0 && (
                    <div className="mt-2 space-y-0.5 text-[11px]">
                      {run.details.map((d, j) => {
                        const meta = OUTCOME_META[d.outcome] || {
                          label: d.outcome,
                          color: "text-muted-foreground bg-muted",
                        };
                        return (
                          <div key={j} className="flex items-center justify-between gap-2">
                            <span className="truncate">{d.nom || d.lead_id}</span>
                            <span className={cn("rounded px-1.5 py-0.5 font-medium", meta.color)}>
                              {meta.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </details>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, highlight }) {
  const highlightClasses = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    blue: "text-blue-700 dark:text-blue-300",
    amber: "text-amber-700 dark:text-amber-300",
    red: "text-red-700 dark:text-red-300",
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn("mt-0.5 text-lg font-bold tabular-nums", highlight && highlightClasses[highlight])}>
        {value ?? "—"}
      </p>
    </div>
  );
}
